import { INTERIOR_WOOD_CATALOG } from "../domain/finishes/finishCatalog";
import type { WoodVariationParams } from "./woodVariation";
/** Visible planks across one exterior albedo tile (seams every u=0.125). */
export const EXTERIOR_TEXTURE_PLANKS_PER_TILE = 8;

export interface MaterialTextureProfile {
	repeat: [number, number];
	tint: number;
	roughness: number;
	envMapIntensity: number;
	anisotropy: number;
	normalScale: [number, number];
	useProceduralMap?: boolean;
	variation?: WoodVariationParams;
}

export const MATERIAL_TEXTURE_PROFILES: Record<
	"framing" | "panel" | "bench" | "exterior" | "stave" | "roof" | "ground",
	MaterialTextureProfile
> = {
	framing: {
		repeat: [2, 7],
		tint: 0xe8d9c4,
		roughness: 0.82,
		envMapIntensity: 0.34,
		anisotropy: 8,
		normalScale: [0.36, 0.36],
	},
	panel: {
		repeat: [3, 12],
		tint: INTERIOR_WOOD_CATALOG.asp.panelLook.tint,
		roughness: INTERIOR_WOOD_CATALOG.asp.panelLook.roughness,
		// Cut from 0.68 — with the real HDRI on balanced/high tiers
		// the interior aspen was reading as overexposed. The wood
		// is diffuse, not reflective, so it doesn't need much IBL
		// fill.
		envMapIntensity: 0.4,
		anisotropy: 10,
		normalScale: [0.18, 0.24],
		// The albedo already contains board seams and grain. A second set of
		// spatial grooves reads as striping, especially on the lying variant.
		variation: {
			grooveStrength: 0,
			grainStrength: 0.014,
			grazingLift: 0.008,
			macroVariation: 0.008,
			brightnessRange: 0.035,
			roughnessRange: 0.035,
		},
	},
	bench: {
		repeat: [0.035, 1],
		tint: INTERIOR_WOOD_CATALOG.asp.benchLook.tint,
		roughness: INTERIOR_WOOD_CATALOG.asp.benchLook.roughness,
		// Cut from 0.82 — same reason as panel; bench tops face up
		// and catch a lot of HDRI sky fill that was blowing out
		// highlights inside the sauna.
		envMapIntensity: 0.5,
		anisotropy: 12,
		normalScale: [0.16, 0.14],
	},
	exterior: {
		repeat: [3, 6],
		tint: 0xd6cab8,
		roughness: 0.8,
		// Bumped from 0.4 — exterior cladding is open to the full sky
		// dome, so it can carry more HDRI fill than the interior aspen
		// (which is intentionally damped via the global env cap and per-
		// profile cuts to prevent cream-wood blow-out).
		envMapIntensity: 0.55,
		anisotropy: 10,
		normalScale: [0.38, 0.26],
	},
	stave: {
		repeat: [5, 1],
		tint: 0xc28f59,
		roughness: 0.76,
		// Bumped from 0.44 — same reason as `exterior`. Barrel staves are
		// always outdoors and need the extra IBL energy to read warm
		// against daylight.
		envMapIntensity: 0.6,
		anisotropy: 10,
		normalScale: [0.42, 0.2],
	},
	roof: {
		repeat: [2, 7],
		tint: 0x2c3136,
		roughness: 0.66,
		// Bumped from 0.58 — roofs catch the strongest sky reflection
		// in the scene and were reading flatter than the cladding.
		envMapIntensity: 0.72,
		anisotropy: 6,
		normalScale: [0.16, 0.24],
	},
	ground: {
		repeat: [48, 48],
		tint: 0x8a9a6c,
		roughness: 0.78,
		envMapIntensity: 0.32,
		anisotropy: 8,
		normalScale: [0.6, 0.6],
		useProceduralMap: false,
	},
};

export const PRESENTATION_MATERIAL_KEYS = [
	"framing",
	"panel",
	"bench",
	"exterior",
	"stave",
] as const;

export const GROUND_TEXTURE_PALETTE = [
	[116, 119, 96],
	[140, 130, 108],
	[97, 103, 82],
	[156, 146, 126],
	[83, 88, 71],
] as const;
